import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const field = (id, label, value, onChange, opts = {}) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}{opts.required && <span className="text-red-500"> *</span>}</Label>
    <Input
      id={id}
      data-testid={`register-${id}-input`}
      type={opts.type || "text"}
      lang="pt-BR"
      value={value}
      onChange={onChange}
      placeholder={opts.placeholder || ""}
      required={opts.required}
      className="h-11"
    />
  </div>
);

export default function Register() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [state, setState] = useState("loading"); // loading | ok | invalid
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [f, setF] = useState({
    name: "", document: "", birth_date: "", phone: "", password: "", confirm: "",
    cep: "", street: "", number: "", complement: "", district: "", city: "", state: "",
  });
  const up = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api
      .get(`/invite/${token}`)
      .then((res) => {
        setInvite(res.data);
        setF((p) => ({ ...p, name: res.data.name || "" }));
        setState("ok");
      })
      .catch((err) => {
        setError(formatApiError(err.response?.data?.detail));
        setState("invalid");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.password.length < 6) return setError("A senha deve ter ao menos 6 caracteres.");
    if (f.password !== f.confirm) return setError("As senhas não coincidem.");
    setSubmitting(true);
    try {
      const { confirm, ...payload } = f;
      const res = await api.post("/register", { token, ...payload });
      setUser(res.data);
      navigate(res.data.role === "instructor" ? "/prof" : "/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading")
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">
        <Loader2 className="h-8 w-8 animate-spin text-[#232D4F]" />
      </div>
    );

  const isInstructor = invite?.role === "instructor";
  const profLabel = { professor: "Professor(a)", instrutor: "Instrutor(a)", mentor: "Mentor(a)", representante: "Representante Credenciado" }[invite?.profile] || "Professor(a)";

  if (state === "invalid")
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-slate-200 p-10 shadow-sm">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-slate-900">Convite inválido</h1>
          <p className="text-slate-500 mt-2">{error || "Este link de convite não é mais válido."}</p>
          <Button className="mt-6" style={{ background: "#232D4F" }} onClick={() => navigate("/login")}>
            Ir para o login
          </Button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6">
        <Brand size="md" />
      </header>

      <div className="max-w-2xl mx-auto px-5 py-10 animate-fade-up">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100" style={{ background: "linear-gradient(90deg,#EBF4FB,#fff)" }}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#1E9E2A] bg-teal-50 px-3 py-1 rounded-full mb-3">
              <CheckCircle2 className="h-3.5 w-3.5" /> Convite válido
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Complete seu cadastro</h1>
            <p className="text-slate-500 text-sm mt-1">
              Bem-vindo(a), <strong>{invite?.name}</strong>. {isInstructor ? "Ative seu acesso de " + profLabel + "." : "Preencha seus dados para ativar seu acesso."}
            </p>
            <p className="text-xs text-slate-400 mt-1">E-mail: {invite?.email}</p>
          </div>

          <form onSubmit={submit} className="p-6 space-y-6">
            <section className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Dados pessoais</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {field("name", "Nome completo", f.name, up("name"), { required: true })}
                {!isInstructor && field("document", "CPF / Documento", f.document, up("document"), { required: true, placeholder: "000.000.000-00" })}
                {!isInstructor && field("birth_date", "Data de nascimento", f.birth_date, up("birth_date"), { required: true, type: "date" })}
                {field("phone", "Telefone / WhatsApp", f.phone, up("phone"), { placeholder: "(00) 00000-0000" })}
              </div>
            </section>

            {!isInstructor && (
              <section className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Endereço</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {field("cep", "CEP", f.cep, up("cep"), { placeholder: "00000-000" })}
                  <div className="sm:col-span-2">{field("street", "Rua / Logradouro", f.street, up("street"))}</div>
                  {field("number", "Número", f.number, up("number"))}
                  {field("complement", "Complemento", f.complement, up("complement"))}
                  {field("district", "Bairro", f.district, up("district"))}
                  {field("city", "Cidade", f.city, up("city"))}
                  {field("state", "Estado (UF)", f.state, up("state"), { placeholder: "SP" })}
                </div>
              </section>
            )}

            <section className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Acesso</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {field("password", "Senha de acesso", f.password, up("password"), { required: true, type: "password", placeholder: "mín. 6 caracteres" })}
                {field("confirm", "Confirmar senha", f.confirm, up("confirm"), { required: true, type: "password" })}
              </div>
            </section>

            {error && (
              <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="register-submit-button"
              disabled={submitting}
              className="w-full h-12 text-white font-semibold"
              style={{ background: "#1E9E2A" }}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Concluir cadastro e acessar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
